import { motion } from "framer-motion";
import { BrainCircuit, Mic, BarChart3, Users, Zap, Share2, BookOpen } from "lucide-react";

const features = [
  {
    icon: BrainCircuit,
    title: "AI-Powered Roleplay",
    description: "GPT-5.2 powered conversations that feel human. The AI adapts to your tone, strategy, and emotional cues.",
    span: "md:col-span-2",
    accent: true,
  },
  {
    icon: BarChart3,
    title: "Performance Scoring",
    description: "Get rated on confidence, persuasion, empathy, and strategic thinking after every session.",
  },
  {
    icon: Zap,
    title: "Real-Time Adaptation",
    description: "The AI adjusts difficulty and responses based on your skill level, ensuring optimal challenge.",
  },
  {
    icon: Users,
    title: "Team Scalability",
    description: "Deploy consistent training across your organization. Track team progress and benchmark performance.",
    span: "md:col-span-2",
    accent: true,
  },
  {
    icon: BookOpen,
    title: "Scenario Library",
    description: "Dozens of pre-built scenarios for sales, negotiation, management, and customer service.",
  },
  {
    icon: Mic,
    title: "Conversation Analytics",
    description: "Deep analysis of your communication patterns, word choice, and persuasion techniques.",
  },
  {
    icon: Share2,
    title: "Shareable Results",
    description: "Share your performance scores with your team, manager, or social network. Drive viral growth.",
  },
];

export default function FeaturesSection() {
  return (
    <section id="features" data-testid="features-section" className="py-[45px] md:py-[90px] stripe-bg relative">
      <div className="absolute inset-0 section-glow-right" />
      <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ ease: [0.22, 1, 0.36, 1] }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <div className="section-overline mb-4">Features</div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mb-6">
            Everything you need<br className="md:hidden" /> to{" "}
            <span className="bg-gradient-to-r from-stripe-primary to-cyan-400 bg-clip-text text-transparent">
              build better communicators
            </span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              className={`${f.span || ""} group`}
            >
              <div
                className={`h-full rounded-2xl p-8 transition-all duration-400 ${
                  f.accent
                    ? "bg-gradient-to-br from-stripe-primary to-indigo-700 text-white shadow-lg shadow-stripe-primary/10 hover:shadow-xl hover:shadow-stripe-primary/15 hover:-translate-y-1"
                    : "stripe-card hover:border-stripe-primary/20"
                }`}
              >
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-all duration-300 group-hover:scale-105 ${
                    f.accent ? "bg-white/20" : "bg-stripe-primary/10"
                  }`}
                >
                  <f.icon className={`w-6 h-6 ${f.accent ? "text-white" : "text-stripe-primary"}`} />
                </div>
                <h3 className={`font-heading text-lg font-semibold mb-2 ${f.accent ? "" : "text-navy"}`}>
                  {f.title}
                </h3>
                <p className={`text-sm leading-relaxed ${f.accent ? "text-indigo-100" : "text-stripe-text-secondary"}`}>
                  {f.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
